import { Component, Input, OnInit } from '@angular/core';
import { CVLocalizationService } from 'src/app/reusables/services/cv-localization.service';
import { ModuleManager } from 'src/shared/models/module-manager';
import { WebhelpETSService } from '../../modules/webhelp-ets/webhelp-ets.service';

@Component({
	selector: 'cv-webhelp-custom-settings',
	templateUrl: './webhelp-ets-settings.component.html',
	styleUrls: ['./webhelp-ets-settings.component.scss']
})
export class WebhelpETSSettingsComponent implements OnInit {
	@Input() moduleKey: string;
	@Input() moduleManager: ModuleManager;

	initialized: boolean = false;
	dataLoading: boolean = false;

	groupByOptions: string[] = [
		"ETS_WRAPUPCATEGORY",
		"ETS_WRAPUPCODE",
		"ETS_KEY",
		"ETS_VALUE"
	];

	dataOptions: string[] = [
		"AGENTSECONDS",
		"ACWSECONDS",
		"AHTSECONDS"
	];

	columnsOptions: string[] = [
		"CONTACTID",
		"ETS_ID_KUND",
		"MEDIATYPE",
		"MENUITEM2934",
		"MENUITEM2935",
		"MENUITEM2938",
		"MENUITEM2943",
		"MENUITEM2969",
		"MENUITEM3955",
		"MENUITEM3956",
		"MENUITEM3957",
		"MENUITEM3958",
		"MENUITEM3959",
		"MENUITEM3961",
		"MENUITEM3962",
		"MENUITEM3963",
		"MENUITEM3964",
		"MENUITEM3965",
		"MENUITEM3966",
		"MENUITEM3967",
		"MENUITEM3968",
		"MENUITEM3969",
		"MENUITEM3970",
		"MENUITEM3971",
		"MENUITEM4044",
		"MENUITEM4083",
		"MENUITEM4085",
		"MENUITEM4086",
		"MENUITEM4087",
		"MENUITEM4089",
		"MENUITEM4090",
		"MENUITEM4092",
		"MENUITEM4095",
		"MENUITEM4096",
		"MENUITEM4098",
		"MENUITEM4099",
		"MENUITEM1403",
		"MENUITEM4110",
		"MENUITEM4121",
		"MENUITEM4122",
		"MENUITEM4123",
		"MENUITEM4124",
		"MENUITEM4126",
		"MENUITEM4127",
		"MENUITEM4141",
		"MENUITEM4142",
		"MENUITEM4143",
		"MENUITEM4144",
		"MENUITEM4145",
		"MENUITEM4149",
		"MENUITEM4150",
		"SKILLID",
		"WRAPTREE"
	];

	groupBySelected: string[];
	dataSelected: string[];
	columnsSelected: string[];

	constructor(
		private cvLocalizationService: CVLocalizationService,
		private service: WebhelpETSService,
	) { }

	async ngOnInit() {
		await this.cvLocalizationService.GetSection("Webhelp_ETS");

		this.moduleManager.registerEvents({
            onSettingsOpened: this.onSettingsOpened.bind(this),
        });

		this.moduleManager.addSetting({
			name: 'groupBy',
			required: true,
			getValue: () => this.groupBySelected,
			onSettingLoaded: (value) => this.groupBySelected = value ?? this.groupByOptions
		});

		this.moduleManager.addSetting({
			name: 'columnsSelected',
			required: true,
			getValue: () => this.columnsSelected,
			onSettingLoaded: (value) => this.columnsSelected = value ?? this.columnsOptions
		});

		this.moduleManager.addSetting({
			name: 'dataColumns',
			required: true,
			getValue: () => this.dataSelected,
			onSettingLoaded: (value) => this.dataSelected = value ?? this.dataOptions
		});

        this.moduleManager.customSettingsAdded = true;

        await this.loadColumns();

        if (!this.initialized) {
            this.columnsSelected = [...this.columnsOptions];
        }

		this.initialized = true;
	}

	/* Provides the options for the Group By and Data selectors */
	async loadGroupByAndData() {
		try {
			let columnStates = await this.moduleManager?.previousStateValues["columnState"];
			if (columnStates != null) {
				this.groupBySelected = [];
				this.dataSelected = [];
				columnStates.forEach(col => {
					if (col.aggFunc != null)
						this.dataSelected.push(col.colId);
					else
						this.groupBySelected.push(col.colId);
				});
			}
		}
		catch (error) { // no state loaded, all columns will be selected in the filter
			this.moduleManager.handleLoadError(error);
		} 
	}

	/* Provides the options for the Columns selector */
	async loadColumns() {
		try {
			// distinct values from the last data pull
			let loadedValues: string[] = await this.moduleManager?.previousStateValues["distinctColumnsFromData"];
			if (loadedValues)
				this.columnsOptions = this.columnsOptions.concat(loadedValues.filter(val => !this.columnsOptions.includes(val)));

			// recent values for the reporting set -- probably won't match the last pull's values
			let recentColumns = await this.service.getDistinctETSColumns(this.moduleKey);
			if (recentColumns){
				this.columnsOptions = this.columnsOptions.concat(recentColumns.data.filter(val => !this.columnsOptions.includes(val)));
            }

			// excluded columns designated through the settings or the ETS_KEY filter
			let excludedColumns: string[] = await this.moduleManager?.previousStateValues["excludedColumns"];
			if (excludedColumns) 
				this.columnsSelected = this.columnsOptions.filter(val => !excludedColumns.includes(val));
				
			this.columnsOptions.sort((a, b) => a.toLocaleLowerCase().localeCompare(b.toLocaleLowerCase()));
		}
		catch (error) { // no state loaded, all columns will be selected in the filter
			this.moduleManager.handleLoadError(error);
		} 
	}

	async onSettingsOpened() {
		this.dataLoading = true;
		this.loadGroupByAndData();
		await this.loadColumns();
		this.dataLoading = false;
	}

	compareWith(obj1: any, obj2: any) {
		return obj1 === obj2;
	}

	getLocalizationString(key: string, defaultValue: string) {
		return this.cvLocalizationService.getCachedText("Webhelp_ETS", key, defaultValue);
    }

    isAllSelected(): boolean {
        return this.columnsSelected.length === this.columnsOptions.length;
    }

    toggleSelectAll(): void {
        if (this.isAllSelected()) {
            this.columnsSelected = [];
        } else {
            this.columnsSelected = this.columnsOptions.slice();
        }
    }
}
